import { IPredefinedCustomDetail } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/PersonalData/usePersonalDataController';
import { RowWrapper } from '@dealstack/frontend/shared/components/DynamicResponsibleTable/RowWrapper';
import { StringInput } from '@dealstack/frontend/shared/components/forms/StringInput/StringInput';
import { TextArea } from '@dealstack/frontend/shared/components/forms/TextArea/TextArea';
import { Button } from '@dealstack/frontend/shared/components/fundament/Button/Button';
import { ButtonsRight } from '@dealstack/frontend/shared/components/fundament/Button/ButtonLayouts';
import {
	CancelButton,
	CloseButtonSquare,
	DeleteButtonSquare,
	SaveButton,
	SaveButtonSquare,
	ThreeDotMenuIconButton,
} from '@dealstack/frontend/shared/components/fundament/Button/CommonButtons';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Modal } from '@dealstack/frontend/shared/components/fundament/Modal/Modal';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { SectionHeader } from '@dealstack/frontend/shared/components/SectionHeader/SectionHeader';
import { Cell, HeaderCell } from '@dealstack/frontend/shared/components/Table/DefaultTable/Cell';
import {
	BodyRow,
	DefaultTable,
	HeaderRow,
} from '@dealstack/frontend/shared/components/Table/DefaultTable/DefaultTable';
import { Row } from '@dealstack/frontend/shared/components/Table/DefaultTable/Row';
import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { generateUUID } from '@dealstack/frontend/shared/other/react/generateUUID';
import { CreateCustomDetailDto } from '@dealstack/shared/lib/generated';
import { CustomDetailEnum } from '@dealstack/shared/lib/generated/models/CustomDetailEnum.ts';
import { yupResolver } from '@hookform/resolvers/yup';
import { Text as MantineText, rem, Tooltip } from '@mantine/core';
import { get } from 'lodash';
import { memo, useEffect, useMemo, useState } from 'react';
import {
	FormProvider,
	useFieldArray,
	useForm,
	useFormContext,
	UseFormReturn,
	useFormState,
} from 'react-hook-form';
import styled from 'styled-components';
import * as Yup from 'yup';
import type {
	ILocalPersonalDataCustomDetails,
	PersonalDataCustomDetailsFormValues,
} from './PersonalDataTagManagerModal.types';

interface Props {
	className?: string;
	onClose: () => unknown;
	data?: ILocalPersonalDataCustomDetails[];

	formController?: UseFormReturn<{
		fieldArray: PersonalDataCustomDetailsFormValues | null;
		predefinedDetails: IPredefinedCustomDetail[];
	}>;
	detailsType: CustomDetailEnum;
	updateCustomDetails: (arg0: CustomDetailEnum) => void;
}

const PersonalDataTagManagerModal = ({
	className,
	onClose,
	data,
	formController,
	detailsType,
	updateCustomDetails,
}: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const commonYup = useCommonYupValidation();
	const personalDataForm = useForm<PersonalDataCustomDetailsFormValues>({
		defaultValues: {
			rows: data?.map((item) => {
				// * This if is required to unify all remote & locally added details in case of removeCustomDetailLocally method on mixed data before save draft
				if (item.id != null) {
					return {
						...item,
						localId: item.id,
					};
				} else {
					return {
						...item,
					};
				}
			}),
		},
		mode: 'all',
		resolver: yupResolver(
			Yup.object().shape({
				rows: Yup.array().of(
					Yup.object().shape({
						name: commonYup.string.required,
					})
				),
			})
		),
	});

	const { fields, append, remove } = useFieldArray({
		name: 'rows',
		control: personalDataForm.control,
	});

	const onModalSaveButtonClicked = () => {
		formController?.setValue('fieldArray', personalDataForm.getValues());
		updateCustomDetails(detailsType);
	};
	const onAddNewDetailButtonClicked = () => {
		append({
			name: '',
			instruction: '',
			type: detailsType,
			localId: generateUUID(),
		} as CreateCustomDetailDto & { localId: string });
	};

	const onRemoveRowClicked = (id: string) => {
		remove(fields.findIndex((item) => item.id === id));
	};
	return (
		<Modal size="md" onClose={onClose} className={className}>
			<SectionHeader
				size="h4b"
				title={tMipCreate('personal_data.tag_manager_modal.title')}
				subtitle={tMipCreate('personal_data.tag_manager_modal.subtitle')}
			/>
			<FormProvider {...personalDataForm}>
				<DefaultTable maxSize withEditableRows>
					<HeaderRow>
						<HeaderCell customSize="1fr">
							{tMipCreate('personal_data.tag_manager_modal.data')}
						</HeaderCell>
						<HeaderCell
							customSize="1fr"
							childrenTooltip={tMipCreate(
								'personal_data.tag_manager_modal.investors_instruction_tooltip'
							)}
						>
							{tMipCreate('personal_data.tag_manager_modal.investors_instruction')}
						</HeaderCell>
						<HeaderCell />
						<HeaderCell size={2.1} />
					</HeaderRow>
					<BodyRow>
						{fields.map((field, index) => {
							return (
								<TagManagerTableRow
									key={field.localId}
									customDetail={field}
									onRemoveClicked={() => {
										onRemoveRowClicked(field.id);
									}}
									index={index}
								/>
							);
						})}
					</BodyRow>
				</DefaultTable>
				<UIElementIdentifier
					identifier={TEST_ELEMENT_IDS.ADD_CUSTOM_DATA_MODAL_ADD_NEW_DETAIL_BUTTON}
				>
					<AddNewDetailButton
						iconSize="lg"
						variant="text"
						icon="add-circle"
						iconPlacement="left"
						onClick={onAddNewDetailButtonClicked}
					>
						{tMipCreate('personal_data.tag_manager_modal.add_button')}
					</AddNewDetailButton>
				</UIElementIdentifier>

				<ModalButtonsRight>
					<UIElementIdentifier
						identifier={TEST_ELEMENT_IDS.ADD_CUSTOM_DATA_MODAL_CANCEL_BUTTON}
					>
						<CancelButton onClick={onClose} />
					</UIElementIdentifier>
					<UIElementIdentifier
						identifier={TEST_ELEMENT_IDS.ADD_CUSTOM_DATA_MODAL_SAVE_BUTTON}
					>
						<SaveButton type="submit" onClick={onModalSaveButtonClicked} />
					</UIElementIdentifier>
				</ModalButtonsRight>
			</FormProvider>
		</Modal>
	);
};
const AddNewDetailButton = styled(Button)``;

const TagManagerTableRow = styled(
	({
		className,
		onRemoveClicked,
		index,
		customDetail,
	}: {
		className?: string;
		onRemoveClicked: () => void;
		index: number;
		customDetail: ILocalPersonalDataCustomDetails;
	}) => {
		const [isEdit, setIsEdit] = useState(false);
		const nameKey = useMemo(() => `rows.${index}.name`, [index]);
		const instructionKey = useMemo(() => `rows.${index}.instruction`, [index]);
		const [originalNameValue, setoriginalNameValue] = useState('');
		const [originalInstructionValue, setoriginalInstructionValue] = useState('');

		const methods = useFormContext();
		methods.watch();
		const nameValue = methods.getValues(nameKey);
		const instructionValue = methods.getValues(instructionKey);
		const { errors } = useFormState();
		const isInvalid = !!get(errors, nameKey);
		const tMipCreate = useTranslationMipCreate();

		const onSaveButtonClicked = async () => {
			const isValid = await methods.trigger(nameKey);
			if (!isValid) {
				return;
			}
			setIsEdit(false);
		};
		const onCloseButtonClicked = () => {
			if (originalNameValue === '') {
				onRemoveClicked();
			} else {
				methods.setValue(nameKey, originalNameValue ?? '');
				methods.setValue(instructionKey, originalInstructionValue ?? '');
				setIsEdit(false);
			}
		};
		const onEditButtonClicked = () => {
			setoriginalNameValue(nameValue);
			setoriginalInstructionValue(instructionValue);
			setIsEdit(true);
		};

		useEffect(() => {
			if (!nameValue && !isEdit) {
				setIsEdit(true);
			} else {
				setoriginalNameValue(nameValue);
				setoriginalInstructionValue(instructionValue);
			}
		}, []);

		return (
			<RowWrapper isInvalid={isInvalid}>
				<Row className={className}>
					<Cell>
						{isEdit ? (
							<UIElementIdentifier
								identifier={TEST_ELEMENT_IDS.ADD_CUSTOM_DATA_MODAL_NAME_INPUT}
								index={index}
							>
								<StringInput
									name={nameKey}
									rhfOptions={{
										required: tMipCreate(
											'personal_data.tag_manager_modal.error_name_required'
										),
									}}
									maxSize
								/>
							</UIElementIdentifier>
						) : (
							<Text variant="p-sm">{nameValue}</Text>
						)}
					</Cell>
					<Cell>
						{isEdit ? (
							<UIElementIdentifier
								identifier={
									TEST_ELEMENT_IDS.ADD_CUSTOM_DATA_MODAL_INSTRUCTIONS_INPUT
								}
								index={index}
							>
								<StringInput name={instructionKey} maxSize />
							</UIElementIdentifier>
						) : (
							<Text elipsis variant="p-sm">
								{instructionValue}
							</Text>
						)}
					</Cell>
					<Cell align="right">
						{(customDetail.isCreatedFromPreFillableForm ||
							customDetail.isCreatedFromGeneratableDocumentTemplate) && (
							<Tooltip
								label={
									<MantineText size="B6Medium">
										{tMipCreate(
											'personal_data.tag_manager_modal.template_warning'
										)}
									</MantineText>
								}
								multiline
								w={220}
							>
								<div style={{ width: 'fit-content', marginTop: '0.25rem' }}>
									<Icon variant="warning-filled" color="orange" size="xl" />
								</div>
							</Tooltip>
						)}
					</Cell>
					{isEdit ? (
						<ButtonsRight gap="sm">
							<UIElementIdentifier
								identifier={TEST_ELEMENT_IDS.ADD_CUSTOM_DATA_MODAL_SAVE_ROW_BUTTON}
								index={index}
							>
								<SaveButtonSquare onClick={onSaveButtonClicked} />
							</UIElementIdentifier>
							<UIElementIdentifier
								identifier={TEST_ELEMENT_IDS.ADD_CUSTOM_DATA_MODAL_CLOSE_ROW_BUTTON}
								index={index}
							>
								<CloseButtonSquare onClick={onCloseButtonClicked} />
							</UIElementIdentifier>
							<UIElementIdentifier
								identifier={
									TEST_ELEMENT_IDS.ADD_CUSTOM_DATA_MODAL_DELETE_ROW_BUTTON
								}
								index={index}
							>
								<DeleteButtonSquare onClick={onRemoveClicked} />
							</UIElementIdentifier>
						</ButtonsRight>
					) : (
						<Cell align="right">
							<UIElementIdentifier
								identifier={
									TEST_ELEMENT_IDS.ADD_CUSTOM_DATA_MODAL_THREE_DOT_MENU_BUTTON
								}
								index={index}
							>
								<ThreeDotMenuIconButton onClick={onEditButtonClicked} />
							</UIElementIdentifier>
						</Cell>
					)}
				</Row>
			</RowWrapper>
		);
	}
)``;
const ModalButtonsRight = styled(ButtonsRight)``;

const Memoized = memo(PersonalDataTagManagerModal);
const Styled = styled(Memoized)`
	${TextArea} {
		width: 100%;
		margin-right: 1em;
	}
	${ButtonsRight} {
		margin-left: 1em;
	}
	${SectionHeader} {
		margin-bottom: 2.5em;
	}
	${ModalButtonsRight} {
		margin-top: 2.5em;
	}
	${DefaultTable} {
		margin-bottom: 1em;
		${BodyRow} {
			min-height: ${rem(50)};
		}
		${TextArea} {
			textarea {
				overflow: hidden;
			}
		}
	}
`;

export { Styled as PersonalDataTagManagerModal };
